import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { FLOATING_ELEMENT_PLACEMENT } from '../../../constants/common'
import { stopPropagation } from '../../../utils/events'
import { AIButtonContainedLight } from '../../AIButton'
import Button, { BUTTON_HEIGHT_24, BUTTON_HEIGHT_32 } from '../../Button'
import { ICON_COLOR, ICON_HEIGHT_32, IconNG } from '../../Icon'
import Text from '../../Text'
import {
  QA_NAME_GRETA_AI_CHAT_CHAT_LIST_TOGGLE,
  QA_NAME_GRETA_AI_CHAT_MINIMIZE,
  QA_NAME_GRETA_AI_CHAT_MODE_TOGGLE,
  QA_NAME_GRETA_AI_CHAT_NEW_CHAT,
} from '../GretaAIChat.constants'
import { useGretaAIChatContext } from '../GretaAIChat.hooks'
import { GretaAIChatChatMenu } from '../GretaAIChatChatMenu'

import * as styles from './GretaAIChatHeader.module.css'

import CollapseIcon from '../../../icons/collapse.svg'
import ExpandIcon from '../../../icons/expand.svg'
import MinimizeIcon from '../../../icons/minimize.svg'
import PlusIcon from '../../../icons/plus.svg'
import GretaAvatar from '../../../icons/unique/ai/avatar.svg'
import ChatListIcon from '../../../icons/unique/ai/sidebar-left.svg'

function GretaAIChatHeader({
  chatListToggleRef,
  isChatListDocked,
  isExpanded,
  onMinimize,
  onToggleMode,
}) {
  const { t } = useTranslation()

  const { actions, chatListLoader, panel } = useGretaAIChatContext()
  const { activeChat } = chatListLoader
  const {
    isChatListColumnOpen,
    isChatListHovered,
    isChatListPinned,
    isLimitReached,
    isLocked,
  } = panel
  const {
    onCloseChatList,
    onDeleteChat,
    onHoverChatList,
    onNewChat,
    onPinChatList,
    onRenameChat,
    onRowMenuOpenChange,
    onToggleChatListColumn,
    onUnhoverChatList,
  } = actions

  const isChatListShown = isChatListDocked
    ? isChatListColumnOpen
    : isChatListPinned || isChatListHovered

  function handleToggleChatList() {
    if (isChatListDocked) {
      onToggleChatListColumn()
    } else if (isChatListPinned) {
      onCloseChatList()
    } else {
      onPinChatList()
    }
  }

  function handleHoverChatList() {
    if (!isChatListDocked) {
      onHoverChatList()
    }
  }

  function handleUnhoverChatList() {
    if (!isChatListDocked) {
      onUnhoverChatList()
    }
  }

  return (
    <header className={styles.header}>
      <span className={styles.titleGroup}>
        {isLocked ? null : (
          <span
            className={styles.chatListToggle}
            onClick={stopPropagation}
            onMouseEnter={handleHoverChatList}
            onMouseLeave={handleUnhoverChatList}
          >
            <Button.Icon
              isGrey
              Icon={ChatListIcon}
              beforeIconProps={{ height: BUTTON_HEIGHT_24 }}
              className={classNames(styles.controlButton, {
                [styles.controlButtonActive]: isChatListShown,
              })}
              elementRef={chatListToggleRef}
              height={BUTTON_HEIGHT_32}
              qaName={QA_NAME_GRETA_AI_CHAT_CHAT_LIST_TOGGLE}
              onClick={handleToggleChatList}
            />
          </span>
        )}
        <IconNG
          Icon={GretaAvatar}
          color={ICON_COLOR.Without}
          size={ICON_HEIGHT_32}
        />
        <Text className={styles.title}>{t('Greta.ChatTitleKey')}</Text>
      </span>
      <div className={styles.controls}>
        {isLocked ? null : (
          <>
            <AIButtonContainedLight
              beforeIconProps={{ Icon: PlusIcon }}
              disabled={isLimitReached}
              qaName={QA_NAME_GRETA_AI_CHAT_NEW_CHAT}
              text={t('Greta.NewChatKey')}
              onClick={onNewChat}
            />
            {activeChat ? (
              <GretaAIChatChatMenu
                buttonHeight={BUTTON_HEIGHT_32}
                chat={activeChat}
                className={styles.controlButton}
                onDelete={onDeleteChat}
                onOpenChange={onRowMenuOpenChange}
                onRename={onRenameChat}
              />
            ) : null}
          </>
        )}
        <Button.Icon
          isGrey
          Icon={isExpanded ? CollapseIcon : ExpandIcon}
          beforeIconProps={{ height: BUTTON_HEIGHT_24 }}
          className={styles.controlButton}
          height={BUTTON_HEIGHT_32}
          qaName={QA_NAME_GRETA_AI_CHAT_MODE_TOGGLE}
          tooltip={isExpanded ? t('Greta.CompactKey') : t('Greta.ExpandKey')}
          tooltipPlacement={FLOATING_ELEMENT_PLACEMENT.bottom}
          onClick={onToggleMode}
        />
        <Button.Icon
          isGrey
          Icon={MinimizeIcon}
          beforeIconProps={{ height: BUTTON_HEIGHT_24 }}
          className={styles.controlButton}
          height={BUTTON_HEIGHT_32}
          qaName={QA_NAME_GRETA_AI_CHAT_MINIMIZE}
          tooltip={t('Greta.MinimizeKey')}
          tooltipPlacement={FLOATING_ELEMENT_PLACEMENT.bottom}
          onClick={onMinimize}
        />
      </div>
    </header>
  )
}

export default GretaAIChatHeader
